import React, { useEffect, useState } from "react";
import {
  Box,
  CardContent,
  Typography,
  CircularProgress
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  CREATE_PAYMENT_ORDER,
  VERIFY_PAYMENT,
  GET_PAYMENTS
} from "../../actions/OrderAction";
import { API } from "../../api";
import {
  StyledPaymentCard,
  StyledAdminHeader,
  StyledPayNowButton,
  StyledColumnBox,
  StyledRowBox,
  StyledTextMonospace,
  StyledHistoryItem,
  StyledHistoryList,
  StyledProgressCentered,
  StyledAdminInfoText,
  StyledStatusText,
  StyledFailureReasonSpan,
  StyledDividerMargin,
  StyledHistoryStatus,
  StyledCircularProgress,
  StyledOrangeProgress,
  StyledGridContainer,
  StyledPaymentFieldLabel,
  StyledPaymentFieldValue,
  StyledTimelineLabel,
  StyledColumnBoxGap,
  StyledHeaderStatus,
  StyledInlineActionsRow,
} from "./style";
import DownloadInvoiceButton from "./DownloadInvoiceButton";
import AdminPaymentCenterPanel from "./AdminPaymentCenterPanel";
import {
  formatPaymentDateTime,
  formatPaymentMethod,
  isManualPaymentRecord,
  hasOrderPaymentReceived,
  resolvePaymentDetails,
  resolvePaymentHistorySummary,
  resolveTransactionId,
} from "../../utils/paymentDisplay";

function PaymentDetailField({ label, value, monospace = false, size = { xs: 12, sm: 6, md: 4 } }) {
  return (
    <Grid size={size}>
      <StyledPaymentFieldLabel>{label}</StyledPaymentFieldLabel>
      {monospace ? (
        <StyledTextMonospace variant="body2" sx={{ wordBreak: "break-all" }}>
          {value || "—"}
        </StyledTextMonospace>
      ) : (
        <StyledPaymentFieldValue variant="body2">{value || "—"}</StyledPaymentFieldValue>
      )}
    </Grid>
  );
}

function PaymentDetailsGrid({ details, formatCurrency }) {
  return (
    <StyledGridContainer container spacing={2} sx={{ mt: 1, mb: 0 }}>
      <PaymentDetailField label="Invoice ID" value={details.invoiceId} monospace />
      <PaymentDetailField label="Transaction ID" value={details.transactionId} monospace />
      {details.referenceNumber && (
        <PaymentDetailField label="Reference Number" value={details.referenceNumber} monospace />
      )}
      <PaymentDetailField label="Payment Mode" value={details.paymentMode} />
      <PaymentDetailField
        label="Payment Amount"
        value={formatCurrency(details.paymentAmount)}
      />
      <PaymentDetailField label="Payment Date" value={details.paymentDate} />
    </StyledGridContainer>
  );
}

// Utility to dynamically load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function CustomerPaymentSection({ orderId, order, adminView, onPaymentSuccess, recipientName, sx = {} }) {
  const dispatch = useDispatch();
  const [localInProgress, setLocalInProgress] = useState(false);

  const orderPayments = useSelector((s) => s.order?.orderPayments);
  const orderPaymentsLoading = useSelector((s) => s.order?.orderPaymentsLoading);

  const fetchPayments = React.useCallback(() => {
    if (orderId) {
      dispatch({ type: GET_PAYMENTS, payload: { orderId } });
    }
  }, [dispatch, orderId]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get("payment");
    const reason = params.get("reason");

    if (paymentStatus === "success") {
      toast.success("Payment verified and completed successfully!");
      fetchPayments();
      if (typeof onPaymentSuccess === "function") {
        onPaymentSuccess();
      }
      const url = new URL(window.location);
      url.searchParams.delete("payment");
      url.searchParams.delete("reason");
      window.history.replaceState({}, document.title, url.pathname + url.search);
    } else if (paymentStatus === "failed") {
      toast.error(reason || "Payment was failed or cancelled.");
      fetchPayments();
      const url = new URL(window.location);
      url.searchParams.delete("payment");
      url.searchParams.delete("reason");
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }
  }, [onPaymentSuccess, fetchPayments]);

  const latestPayment = orderPayments?.payments?.[0] || null;

  // Determine current display payment state
  // States: pending, in_progress, success, failed
  let displayState = "pending";
  let failureReason = "";

  const orderStatus = String(order?.order_status || "").toLowerCase();
  const orderPaymentStatus = String(order?.payment_status || "").toLowerCase();
  const isFullyPaid = orderStatus === "paid" || orderPaymentStatus === "paid";
  const isPartialPaid = orderPaymentStatus === "partial_paid";

  if (isFullyPaid) {
    displayState = "success";
  } else if (isPartialPaid) {
    displayState = "partial_paid";
  } else if (
    latestPayment?.status === "success" &&
    !isManualPaymentRecord(latestPayment)
  ) {
    displayState = "success";
  } else if (localInProgress) {
    displayState = "in_progress";
  } else if (
    latestPayment?.status === "failed" ||
    latestPayment?.status === "in_progress" ||
    latestPayment?.status === "pending"
  ) {
    displayState = "failed";
    failureReason = latestPayment?.failure_reason || "Payment was cancelled or not completed";
  } else {
    displayState = "pending";
  }

  const formatCurrency = (val) => {
    const num = Number(val || 0);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2
    }).format(num);
  };

  function formatStatusText(status, failureReason) {
    if (!status) return "—";
    const str = String(status).toLowerCase();
    if (str === "success" || str === "paid") return "Success";
    if (str === "in_progress") return "In Progress";
    if (str === "failed") {
      if (
        failureReason === "Cancelled by user" ||
        String(failureReason || "").toLowerCase().includes("cancelled") ||
        String(failureReason || "").toLowerCase().includes("dismiss")
      ) {
        return "Cancelled";
      }
      return "Failed";
    }
    const formatted = str.replace(/_/g, " ");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  const displayStatusVal =
    displayState === "partial_paid"
      ? "Partial Paid"
      : formatStatusText(
          displayState === "failed" ? "failed" : displayState,
          latestPayment?.failure_reason
        );
  const statusKeyForBadge =
    displayState === "partial_paid"
      ? "partial_paid"
      : displayStatusVal === "Cancelled"
        ? "cancelled"
        : displayState;

  const paymentDetails = resolvePaymentDetails(order, orderPayments?.payments || []);
  const showAdminPaymentCenterInvoiceDownload = hasOrderPaymentReceived(
    order,
    orderPayments?.payments || []
  );

  const handleRazorpayCheckout = async (rzpOrderData) => {
    const isScriptLoaded = await loadRazorpayScript();
    if (!isScriptLoaded) {
      toast.error("Failed to load Razorpay SDK. Check your internet connection.");
      setLocalInProgress(false);
      return;
    }

    try {
      const rawData = rzpOrderData?.data || rzpOrderData;
      if (orderId && rawData.razorpay_order_id) {
        API.logPaymentEvent(orderId, {
          event_type: "checkout_opened",
          razorpay_order_id: rawData.razorpay_order_id
        }).catch((err) => console.error("Failed to log event:", err));
      }
      const options = {
        key: rawData.key,
        amount: rawData.amount,
        currency: rawData.currency,
        name: "NRI to Native",
        description: `Order Payment for ${rawData.order_ref}`,
        order_id: rawData.razorpay_order_id,
        handler: function (response) {
          setLocalInProgress(true);
          // Dispatch verify action
          dispatch({
            type: VERIFY_PAYMENT,
            payload: {
              orderId,
              data: {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              },
              onSuccess: () => {
                setLocalInProgress(false);
                toast.success("Payment verified and completed successfully!");
                fetchPayments();
                if (typeof onPaymentSuccess === "function") {
                  onPaymentSuccess();
                }
              },
              onError: (err) => {
                setLocalInProgress(false);
                toast.error(err?.data?.message || err?.message || "Signature verification failed.");
                fetchPayments();
              }
            }
          });
        },
        modal: {
          ondismiss: function () {
            setLocalInProgress(false);
            toast.error("Payment checkout cancelled.");
            if (orderId && rawData.razorpay_order_id) {
              API.logPaymentEvent(orderId, {
                event_type: "checkout_cancelled",
                razorpay_order_id: rawData.razorpay_order_id
              }).then(() => {
                fetchPayments();
              }).catch((err) => {
                console.error("Failed to log checkout cancellation:", err);
                fetchPayments();
              });
            } else {
              fetchPayments();
            }
          }
        },
        prefill: {
          name: order?.user?.name || order?.create_by_user?.name || "",
          email: order?.user?.email || order?.create_by_user?.email || "",
          contact: (order?.user?.phone_number || order?.create_by_user?.phone_number || "").replace(/\D/g, "")
        },
        theme: {
          color: "#FF8500"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Razorpay initiation error:", err);
      toast.error("Failed to open payment window: " + (err.description || err.message));
      setLocalInProgress(false);
    }
  };

  const initiatePayment = () => {
    setLocalInProgress(true);
    dispatch({
      type: CREATE_PAYMENT_ORDER,
      payload: {
        orderId,
        onSuccess: (data) => {
          handleRazorpayCheckout(data);
        },
        onError: (err) => {
          setLocalInProgress(false);
          toast.error(err?.data?.message || err?.message || "Failed to initiate payment");
        }
      }
    });
  };


  const shouldShowCard =
    orderStatus === "bill_generated" ||
    orderStatus === "paid" ||
    (orderPayments?.payments && orderPayments.payments.length > 0);

  if (!shouldShowCard) {
    return null;
  }

  if (orderPaymentsLoading && !orderPayments) {
    return (
      <StyledPaymentCard elevation={0} sx={sx}>
        <CardContent>
          <StyledProgressCentered>
            <StyledCircularProgress size={24} />
          </StyledProgressCentered>
        </CardContent>
      </StyledPaymentCard>
    );
  }

  if (adminView) {
    return (
      <StyledPaymentCard elevation={0} sx={sx}>
        <StyledAdminHeader>
          <Typography variant="subtitle1" fontWeight={700} color="#333">
            Admin Payment Center
          </Typography>
          {showAdminPaymentCenterInvoiceDownload && (
            <DownloadInvoiceButton
              order={order}
              recipientName={recipientName}
              isAdmin={adminView}
            />
          )}
        </StyledAdminHeader>
        <CardContent>
          {!orderPayments?.payments || orderPayments.payments.length === 0 ? (
            <StyledAdminInfoText variant="body2" align="center">
              No payment attempts found for this order yet.
            </StyledAdminInfoText>
          ) : (
            <AdminPaymentCenterPanel
              payments={orderPayments.payments}
              invoiceId={paymentDetails.invoiceId}
              formatCurrency={formatCurrency}
              formatStatusText={formatStatusText}
            />
          )}
        </CardContent>
      </StyledPaymentCard>
    );
  }

  return (
    <StyledPaymentCard elevation={0} sx={sx}>
      <StyledAdminHeader>
        <Typography variant="subtitle1" fontWeight={700} color="#333">
          Payment Details
        </Typography>
        <StyledInlineActionsRow>
          {displayState !== "pending" && (
            <StyledHeaderStatus status={statusKeyForBadge}>
              {displayStatusVal}
            </StyledHeaderStatus>
          )}
          <DownloadInvoiceButton order={order} recipientName={recipientName} />
        </StyledInlineActionsRow>
      </StyledAdminHeader>
      <CardContent>
        <StyledColumnBox>
          {displayState === "pending" && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <StyledStatusText variant="subtitle1" fontWeight={700} textcolor="#FF8500">
                Payment Pending
              </StyledStatusText>
              <PaymentDetailsGrid details={paymentDetails} formatCurrency={formatCurrency} />
              <StyledPayNowButton
                variant="contained"
                onClick={initiatePayment}
                disabled={localInProgress}
                sx={{ mt: 1 }}
              >
                {localInProgress ? <CircularProgress size={20} color="inherit" /> : "Pay Now"}
              </StyledPayNowButton>
            </Box>
          )}

          {displayState === "in_progress" && (
            <StyledColumnBox>
              <StyledRowBox>
                <StyledOrangeProgress size={24} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#FF8500">
                    Payment In Progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please complete the payment in the Razorpay checkout window...
                  </Typography>
                </Box>
              </StyledRowBox>
              <PaymentDetailsGrid details={paymentDetails} formatCurrency={formatCurrency} />
            </StyledColumnBox>
          )}

          {displayState === "partial_paid" && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <StyledStatusText variant="subtitle1" fontWeight={700} textcolor="#7B61FF" sx={{ mb: 0.5 }}>
                Partial Payment Received
              </StyledStatusText>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Please pay the remaining balance to complete your order.
              </Typography>
              <PaymentDetailsGrid details={paymentDetails} formatCurrency={formatCurrency} />
              <StyledPayNowButton
                variant="contained"
                onClick={initiatePayment}
                disabled={localInProgress}
                sx={{ mt: 1 }}
              >
                {localInProgress ? <CircularProgress size={20} color="inherit" /> : "Pay Now"}
              </StyledPayNowButton>
            </Box>
          )}

          {displayState === "success" && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <StyledStatusText variant="subtitle1" fontWeight={700} textcolor="#00C046" sx={{ mb: 1 }}>
                Payment Successful
              </StyledStatusText>
              <PaymentDetailsGrid details={paymentDetails} formatCurrency={formatCurrency} />
            </Box>
          )}

          {displayState === "failed" && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <StyledStatusText variant="subtitle1" fontWeight={700} textcolor={displayStatusVal === "Cancelled" ? "#455a64" : "#FF3850"}>
                {displayStatusVal === "Cancelled" ? "Payment Cancelled" : "Payment Failed"}
              </StyledStatusText>
              <StyledTimelineLabel variant="body2" color="text.secondary">
                Reason: <StyledFailureReasonSpan component="span">{failureReason}</StyledFailureReasonSpan>
              </StyledTimelineLabel>
              <PaymentDetailsGrid
                details={{
                  ...paymentDetails,
                  transactionId: resolveTransactionId(latestPayment) || paymentDetails.transactionId,
                  referenceNumber: latestPayment?.reference_number || paymentDetails.referenceNumber,
                  paymentMode: formatPaymentMethod(latestPayment?.payment_method, latestPayment) || paymentDetails.paymentMode,
                  paymentDate: formatPaymentDateTime(latestPayment) !== "—"
                    ? formatPaymentDateTime(latestPayment)
                    : paymentDetails.paymentDate,
                  paymentAmount: latestPayment?.amount ?? paymentDetails.paymentAmount,
                }}
                formatCurrency={formatCurrency}
              />
              <StyledPayNowButton
                variant="contained"
                onClick={initiatePayment}
                disabled={localInProgress}
                sx={{ mt: 1 }}
              >
                {localInProgress ? <CircularProgress size={20} color="inherit" /> : "Pay Now"}
              </StyledPayNowButton>
            </Box>
          )}

          {orderPayments?.payments && orderPayments.payments.length > 1 && (
            <Box>
              <StyledDividerMargin />
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                Payment History
              </Typography>
              <StyledHistoryList>
                {orderPayments.payments.slice(1).map((pay, i) => (
                  <StyledHistoryItem key={pay.id || i}>
                    <Box>
                      <Typography variant="caption" display="block" fontWeight={600} color="#555">
                        {resolvePaymentHistorySummary(pay)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(pay.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                    {(() => {
                      const text = formatStatusText(pay.status, pay.failure_reason);
                      const color = text === "Success" ? "#00C046" : (text === "Cancelled" ? "#455a64" : "#FF3850");
                      return (
                        <StyledHistoryStatus
                          variant="caption"
                          fontWeight={700}
                          textcolor={color}
                        >
                          {text}
                        </StyledHistoryStatus>
                      );
                    })()}
                  </StyledHistoryItem>
                ))}
              </StyledHistoryList>
            </Box>
          )}
        </StyledColumnBox>
      </CardContent>
    </StyledPaymentCard>
  );
}
